/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { appSuitesApi } from '../services/api/appSuites'
import { isFlagEffectivelyEnabled } from '../constants/featureFlags'
import {
  EXPERIENCE_STORAGE_KEY,
  SUITE_CACHE_KEY,
} from '../constants/appSuites'
import { useAuthValue } from './AuthContext'
import { useFeatureFlags } from './FeatureFlagsContext'
import { useBranding } from './UiCustomizationContext'
import type { AppExperience, AppSuiteEffective } from '../types/appSuites'
import { resolveExperience } from '../utils/appSuites/resolveExperience'
import { logger } from '@utils/logger'

interface AppSuiteSettingsValue {
  suite: AppSuiteEffective | null
  experience: AppExperience
  isResolving: boolean
  userChoice: AppExperience | null
  setUserChoice: (choice: AppExperience) => void
}

const AppSuiteSettingsContext = createContext<AppSuiteSettingsValue | undefined>(undefined)

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

function readChoice(): AppExperience | null {
  try {
    const stored = localStorage.getItem(EXPERIENCE_STORAGE_KEY)
    return stored === 'classic' || stored === 'apps' ? stored : null
  } catch {
    return null
  }
}

export function AppSuiteSettingsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuthValue()
  const { platform_only: platformOnly } = useBranding()
  const { flags, isLoading: flagsLoading, error: flagsError } = useFeatureFlags()
  const queryClient = useQueryClient()
  const [userChoice, setChoiceState] = useState<AppExperience | null>(readChoice)
  const flagOn = isFlagEffectivelyEnabled('app_suites_view', flags)
  const cached = readJson<AppSuiteEffective>(SUITE_CACHE_KEY)

  const query = useQuery({
    queryKey: ['app-suites', 'effective'],
    queryFn: appSuitesApi.getEffective,
    enabled: isAuthenticated && !platformOnly && flagOn && !flagsLoading,
    staleTime: 5 * 60_000,
    retry: 1,
    initialData: cached ?? undefined,
    initialDataUpdatedAt: cached ? 0 : undefined,
  })

  useEffect(() => {
    if (query.data && !query.isError) {
      try {
        localStorage.setItem(SUITE_CACHE_KEY, JSON.stringify(query.data))
      } catch {
        logger.warn('app_suites: could not cache the effective map')
      }
    }
  }, [query.data, query.isError])

  useEffect(() => {
    if (query.isError) {
      localStorage.removeItem(SUITE_CACHE_KEY)
      queryClient.removeQueries({ queryKey: ['app-suites', 'effective'] })
    }
  }, [query.isError, queryClient])

  // A failed flags request leaves every flag at its fail-closed default (off). That is
  // "unknown", not "disabled" — never treat it as the flag being off, or one transient
  // /auth/me/features failure wipes the saved cache + choice and the next refresh
  // starts with nothing to render from.
  const flagsKnownOff = !flagsLoading && !flagsError && !platformOnly && !flagOn

  const suppressChoice = flagsKnownOff
  if (suppressChoice && userChoice !== null) {
    setChoiceState(null)
  }

  useEffect(() => {
    if (!flagsKnownOff) return
    try {
      localStorage.removeItem(SUITE_CACHE_KEY)
      localStorage.removeItem(EXPERIENCE_STORAGE_KEY)
    } catch {
      logger.warn('app_suites: could not clear a stored experience')
    }
  }, [flagsKnownOff])

  const setUserChoice = useCallback((choice: AppExperience) => {
    setChoiceState(choice)
    try {
      localStorage.setItem(EXPERIENCE_STORAGE_KEY, choice)
    } catch {
      logger.warn('app_suites: could not store the experience choice')
    }
  }, [])

  const suite = query.isError ? null : (query.data ?? null)
  const effectiveChoice = suppressChoice ? null : userChoice
  const experience = resolveExperience({
    platformOnly,
    flagOn,
    suite,
    userChoice: effectiveChoice,
  })

  // `experience` reads "classic" whenever flags or the suite have not arrived yet, so it
  // cannot be trusted until we actually know. Report "resolving" for every unknown state
  // — not only when a cache predicts Four apps — so the layout never draws the Classic
  // sidebar during a refresh, a fresh browser, or a wiped cache.
  //  1. Flags still loading: we cannot tell whether Four apps is on.
  //  2. Flag on but the effective map has never arrived (no cache, first fetch pending).
  // With a cached map we render from it immediately and revalidate in the background.
  const knownClassic =
    effectiveChoice === 'classic' && (suite ?? cached)?.allow_experience_switch === true
  const suiteUnknown = isAuthenticated && flagOn && !query.data && !query.isError
  const isResolving = !platformOnly && !knownClassic && (flagsLoading || suiteUnknown)

  const value = useMemo<AppSuiteSettingsValue>(
    () => ({ suite, experience, isResolving, userChoice, setUserChoice }),
    [suite, experience, isResolving, userChoice, setUserChoice],
  )

  return (
    <AppSuiteSettingsContext.Provider value={value}>{children}</AppSuiteSettingsContext.Provider>
  )
}

export function useAppSuiteSettings(): AppSuiteSettingsValue {
  const context = useContext(AppSuiteSettingsContext)
  if (context === undefined) {
    throw new Error('useAppSuiteSettings must be used within an AppSuiteSettingsProvider')
  }
  return context
}
